import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXNFull, fmtDate } from "@/lib/api";
import { X, History, Loader2, UserCheck, FileDown } from "lucide-react";
import { toast } from "sonner";

const CHIP = {
  borrador: { label: "BORRADOR", color: "#94a3b8" },
  aprobada: { label: "APROBADA", color: "var(--green)" },
  rechazada: { label: "RECHAZADA", color: "var(--red)" },
  terminada: { label: "TERMINADA", color: "var(--cyan)" },
  pagada: { label: "PAGADA", color: "var(--magenta)" },
};

export default function WorkshopHistoryModal({ vehicleId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/workshop/vehicles/${vehicleId}/history/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expediente_${data?.vehicle?.plates || vehicleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Expediente PDF descargado");
    } catch { toast.error("Error al generar el PDF"); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    api.get(`/workshop/vehicles/${vehicleId}/history`).then(({ data: d }) => setData(d)).catch(() => onClose());
  }, [vehicleId, onClose]);

  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="panel-strong w-full max-w-2xl overflow-y-auto scan-line" style={{ maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()} data-testid="vehicle-history-modal">
        <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="label-mono">// HISTORIAL DEL VEHÍCULO</div>
            <h2 className="font-display font-bold text-xl text-slate-100 flex items-center gap-2">
              <History className="w-5 h-5" style={{ color: "var(--cyan)" }} />
              {data ? `${data.vehicle.brand} ${data.vehicle.model} ${data.vehicle.year || ""}` : "Cargando…"}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <button onClick={downloadPdf} disabled={downloading} data-testid="history-pdf-btn"
                className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50"
                style={{ color: "var(--green)", borderColor: "rgba(34,197,94,0.4)" }}>
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Expediente PDF
              </button>
            )}
            <button onClick={onClose} className="text-slate-500 hover:text-cyan-300" data-testid="history-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-mono text-slate-400">
              <span style={{ color: "var(--cyan)" }}>{data.vehicle.plates}</span>
              {data.vehicle.vin && <span>VIN {data.vehicle.vin}</span>}
              {data.vehicle.km != null && <span>KM {Number(data.vehicle.km).toLocaleString("es-MX")}</span>}
              {data.vehicle.color && <span>{data.vehicle.color}</span>}
              {data.vehicle.custodian && (
                <span className="flex items-center gap-1" style={{ color: "var(--amber)" }} data-testid="history-custodian">
                  <UserCheck className="w-3.5 h-3.5" /> Resguardo: {data.vehicle.custodian}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="history-summary">
              {[
                ["SERVICIOS", data.summary.services, "var(--cyan)"],
                ["GASTADO", fmtMXNFull(data.summary.total_spent), "var(--amber)"],
                ["PAGADO", fmtMXNFull(data.summary.total_paid), "var(--magenta)"],
                ["ÚLTIMO SERVICIO", data.summary.last_service ? fmtDate(data.summary.last_service) : "—", "var(--text)"],
              ].map(([label, value, color]) => (
                <div key={label} className="panel p-3">
                  <div className="label-mono" style={{ color: "var(--text-mute)" }}>{label}</div>
                  <div className="font-display font-bold text-lg tabular-nums" style={{ color }}>{value}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2" data-testid="history-timeline">
              {data.quotes.length === 0 && <div className="text-center py-8 text-slate-500 panel">Este vehículo aún no tiene servicios registrados.</div>}
              {data.quotes.map((q) => {
                const chip = CHIP[q.status] || CHIP.borrador;
                return (
                  <button key={q.id} data-testid={`history-quote-${q.id}`}
                    onClick={() => { onClose(); navigate(`/workshop/quotes/${q.id}`); }}
                    className="w-full text-left panel p-3 row-hover flex items-start gap-3">
                    <div className="shrink-0 w-2 h-2 rounded-full mt-1.5" style={{ background: chip.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs" style={{ color: "var(--cyan)" }}>{q.folio}</span>
                        <span className="font-mono text-[11px] text-slate-400">{fmtDate(q.date)}</span>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest" style={{ color: chip.color, border: `1px solid ${chip.color}` }}>{chip.label}</span>
                        <span className="ml-auto font-mono text-sm text-slate-100">{fmtMXNFull(q.total)}</span>
                      </div>
                      <div className="text-xs text-slate-300 mt-0.5 truncate">{q.service_description || (q.items || []).map((i) => i.concept).join(", ") || "—"}</div>
                      {(q.items || []).length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5">{q.items.length} conceptos{q.payment_method ? ` · pagado por ${q.payment_method}` : ""}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
